"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Award, CircleCheck as CheckCircle2, Clock, CircleQuestionMark as HelpCircle, PhoneCall, ShieldCheck, Star, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
const deltaFaqs = [{
  id: "item-1",
  question: "Do you use genuine Delta OEM replacement parts?",
  answer: "Yes. We stock and install original Delta cartridges, Diamond Seal valves, Touch2O solenoid packs, and trim kits to preserve your factory warranty and ensure exact fit and performance."
}, {
  id: "item-2",
  question: "How do I fix a dripping Delta single-handle shower valve?",
  answer: "Drips typically result from mineral buildup or wear inside the Delta RP46074 or RP19804 pressure balance cartridge. Our technicians replace the cartridge, flush the valve body, and inspect the seats and springs in a single visit."
}, {
  id: "item-3",
  question: "Can you troubleshoot Delta Touch2O or VoiceIQ electronic faucets?",
  answer: "Our licensed technicians carry diagnostic equipment specifically for Delta Touch2O battery boxes, grounding wires, and solenoid modules to solve false activations, low flow, or LED indicator errors."
}, {
  id: "item-4",
  question: "Will repairing my Delta fixture void my Delta Lifetime Warranty?",
  answer: "No. Delta's Lifetime Limited Warranty covers parts for original homeowner installations. Professional installation and repair by licensed plumbers protect your product warranty and ensure plumbing code compliance."
}, {
  id: "item-5",
  question: "What should I do if my Delta kitchen pull-down spray wand has low water pressure?",
  answer: "Low pressure in pull-down faucets is usually caused by sediment inside the aerator or the check valve within the quick-connect hose assembly. We can clear, service, or replace the wand assembly on the spot."
}];
const testimonials = [{
  id: 1,
  name: "Marcus Holloway",
  location: "Jonesboro, GA",
  initials: "MH",
  rating: 5,
  title: "Delta Shower Valve Replacement",
  comment: "Our Delta Monitor shower valve stopped regulating temperature late on a Sunday. Jonesboro Emergency Plumber arrived within 45 minutes, had the exact cartridge on the truck, and fixed it cleanly."
}, {
  id: 2,
  name: "Brenda Vance",
  location: "Lake Spivey, GA",
  initials: "BV",
  rating: 5,
  title: "Delta Touch2O Faucet Repair",
  comment: "Other plumbers told me to replace the entire faucet when the sensor stopped responding. Their team pinpointed a faulty solenoid wire in minutes and saved me hundreds of dollars."
}];
const trustStats = [{
  id: 1,
  icon: Clock,
  value: "20+ Years",
  label: "Delta Fixture Experience"
}, {
  id: 2,
  icon: ShieldCheck,
  value: "100% Genuine",
  label: "OEM Delta Cartridges & Parts"
}, {
  id: 3,
  icon: Award,
  value: "Master Licensed",
  label: "Georgia State Plumbing License"
}, {
  id: 4,
  icon: CheckCircle2,
  value: "24/7 Rapid",
  label: "Emergency Dispatch in Clayton Co."
}];
const certifications = [{
  id: 1,
  name: "Certified Delta Product Specialists",
  detail: "Trained on Diamond Seal Technology, MultiChoice Universal Valves, and Touch2O electronics."
}, {
  id: 2,
  name: "Georgia Master Plumber License #MP209411",
  detail: "Fully insured and bonded for residential and commercial fixture service."
}, {
  id: 3,
  name: "Factory-Spec Workmanship Guarantee",
  detail: "All repair and installation labor backed by a comprehensive 1-year service warranty."
}];
export default function DeltaFaq() {
  const prefersReducedMotion = useReducedMotion();
  const animationProps = prefersReducedMotion ? {
    initial: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: "-40px"
    },
    transition: {
      duration: 0.45
    }
  };
  return <section id="deltafaqandtrust" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div {...animationProps} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-semibold text-secondary-foreground border border-border">
            <Wrench className="w-3.5 h-3.5 text-accent" />
            Delta Faucet Specialists
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Delta Fixtures FAQ &amp; Verified Trust
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear answers about Delta kitchen, bath, and shower systems in Jonesboro,
            backed by two decades of hands-on fixture repair and genuine factory parts.
          </p>
        </motion.div>

        {/* Trust Stats Counter Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-14 md:mb-18">
          {trustStats.map((stat, i) => {
          const Icon = stat.icon;
          return <motion.div key={stat.id} data-index={i} {...animationProps} className="bg-card text-card-foreground border border-border rounded-xl p-5 sm:p-6 text-center shadow-xs flex flex-col items-center justify-center">
                <div className="size-11 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div className="text-2xl sm:text-3xl font-bold font-default text-foreground mb-1">
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground font-medium">
                  {stat.label}
                </div>
              </motion.div>;
        })}
        </div>

        {/* 2-Column Section: FAQ on Left, Testimonials & Trust Cards on Right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: FAQ Accordion */}
          <motion.div {...animationProps} className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-6">
              <HelpCircle className="w-6 h-6 text-primary shrink-0" />
              <h3 className="text-2xl font-bold font-default text-foreground">
                Frequently Asked Delta Questions
              </h3>
            </div>

            <Card className="border border-border shadow-xs bg-card py-2">
              <CardContent className="p-4 sm:p-6">
                <Accordion type="single" collapsible className="w-full">
                  {deltaFaqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-border px-1 py-1">
                      <AccordionTrigger className="text-base font-semibold text-foreground hover:no-underline text-left py-4">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed pt-1 pb-4">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </CardContent>
            </Card>

            {/* Quick Emergency CTA Strip */}
            <div className="mt-6 p-5 rounded-xl bg-secondary border border-border flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="font-semibold text-secondary-foreground text-sm sm:text-base">
                  Have an urgent Delta faucet or shower leak?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  Our emergency vans in Jonesboro are stocked with Delta valves right now.
                </p>
              </div>
              <Button asChild size="sm" className="w-full sm:w-auto shrink-0 shadow-xs">
                <a href="tel:4708479076" className="flex items-center gap-2">
                  <PhoneCall className="w-4 h-4" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Certifications & Customer Testimonials */}
          <motion.div {...animationProps} className="lg:col-span-5 space-y-6">
            {/* Certifications Card */}
            <Card className="border border-border shadow-xs bg-card">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-accent" />
                  <CardTitle className="text-lg sm:text-xl font-bold font-default text-foreground">
                    Certifications &amp; Standards
                  </CardTitle>
                </div>
                <CardDescription className="text-xs sm:text-sm text-muted-foreground">
                  Strict compliance with Delta installation specifications and local plumbing codes.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-1">
                {certifications.map((cert, i) => <div key={cert.id} data-index={i} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground block">
                        {cert.name}
                      </span>
                      <span className="text-xs sm:text-sm text-muted-foreground block mt-0.5">
                        {cert.detail}
                      </span>
                    </div>
                  </div>)}
              </CardContent>
            </Card>

            {/* Verified Customer Testimonials */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold font-default text-foreground flex items-center gap-2">
                  <Star className="w-4 h-4 fill-accent text-accent" />
                  Recent Delta Service Reviews
                </h3>
                <Badge variant="outline" className="text-xs border-border">
                  Verified Local Jobs
                </Badge>
              </div>

              <div className="space-y-4">
                {testimonials.map((review, i) => <Card key={review.id} data-index={i} className="border border-border shadow-xs bg-card p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1">
                        {[0, 1, 2, 3, 4].map(starIdx => <Star key={starIdx} className="w-3.5 h-3.5 fill-accent text-accent" />)}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {review.location}
                      </span>
                    </div>
                    <h4 className="font-semibold text-foreground text-sm mb-1.5">
                      {review.title}
                    </h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">
                      "{review.comment}"
                    </p>
                    <Separator className="my-2 bg-border" />
                    <div className="flex items-center gap-2.5 pt-1">
                      <SafeHydrate><Avatar className="size-7 bg-muted border border-border">
                        <AvatarFallback className="text-[10px] font-bold text-foreground">
                          {review.initials}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <div>
                        <p className="text-xs font-semibold text-foreground leading-none">
                          {review.name}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Verified Homeowner
                        </p>
                      </div>
                    </div>
                  </Card>)}
              </div>
            </div>

            {/* Bottom Nav Link */}
            <div className="pt-2 text-center sm:text-left">
              <Link to="/faucet-sink-repair/" className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1.5">
                <span>Explore all faucet &amp; sink repair services</span>
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}